import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageHeader, Section } from '@/components/PageHeader.jsx';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { Select } from '@/components/ui/Select.jsx';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table.jsx';
import { useToast } from '@/components/ui/Toast.jsx';
import { Plus, Trash2 } from 'lucide-react';

export default function TaxTablesPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data = [] } = useQuery({ queryKey: ['taxTables'], queryFn: () => api.get('/tax-tables') });

  const yearRow = data.find((r) => r.year === Number(year));
  const slabs = yearRow ? JSON.parse(yearRow.slabs_json) : [];
  const [draft, setDraft] = useState(null);
  const editing = draft ?? slabs;

  const save = useMutation({
    mutationFn: (s) => api.put(`/tax-tables/${year}`, { slabs: s }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['taxTables'] }); setDraft(null); toast({ variant: 'success', title: 'Saved' }); },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  const update = (i, k, v) => {
    const next = (draft ?? slabs).map((s, idx) => (idx === i ? { ...s, [k]: k === 'rate' ? Number(v) : (v === '' ? null : Number(v)) } : s));
    setDraft(next);
  };
  const addRow = () => setDraft([...(draft ?? slabs), { up_to: 0, rate: 0, subtract: 0 }]);
  const removeRow = (i) => setDraft((draft ?? slabs).filter((_, idx) => idx !== i));

  return (
    <div className="space-y-5">
      <PageHeader title="APIT Tax Tables" description="Progressive monthly tax slabs" />
      <Section
        title={`Slabs for ${year}`}
        actions={
          <div className="flex items-center gap-2">
            <FormField><Input type="number" value={year} onChange={(e) => { setYear(Number(e.target.value)); setDraft(null); }} className="w-24" /></FormField>
            <Button variant="outline" onClick={addRow}><Plus className="h-4 w-4" />Add Slab</Button>
            <Button onClick={() => save.mutate(editing)} disabled={!draft || save.isPending}>Save Year</Button>
          </div>
        }
      >
        <p className="mb-3 text-xs text-slate-500">
          Each row: monthly income up to <code>up_to</code> (LKR) is taxed at <code>rate</code> (decimal, e.g. 0.06 for 6%) minus <code>subtract</code>. Use <code>null</code> in <code>up_to</code> for the top bracket.
        </p>
        <Table>
          <THead><TR><TH>Up To (LKR/month)</TH><TH>Rate (decimal)</TH><TH>Subtract (LKR)</TH><TH /></TR></THead>
          <TBody>
            {editing.length === 0 ? (
              <TR><TD colSpan={4} className="text-center py-4 text-slate-500">No slabs defined for {year}</TD></TR>
            ) : editing.map((s, i) => (
              <TR key={i}>
                <TD><Input type="number" value={s.up_to ?? ''} placeholder="∞" onChange={(e) => update(i, 'up_to', e.target.value)} /></TD>
                <TD><Input type="number" step="0.001" value={s.rate} onChange={(e) => update(i, 'rate', e.target.value)} /></TD>
                <TD><Input type="number" value={s.subtract ?? 0} onChange={(e) => update(i, 'subtract', e.target.value)} /></TD>
                <TD><Button variant="ghost" size="icon" onClick={() => removeRow(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button></TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {draft && <p className="mt-2 text-xs text-amber-600">Unsaved changes — click Save Year.</p>}
      </Section>
    </div>
  );
}
